// El botón de emoticonos — uno solo, para el editor de correo y el de notas.
//
// Lissette, 2026-09-03: "agrega la opción de colocar emoticón en todos lados
// donde se redacte correo y notas".
//
// POR QUE ES UN COMPONENTE Y NO DOS BOTONES. Los dos editores del CRM son
// distintos por dentro (uno emite HTML, el otro marcas de nota) pero para esto
// necesitan exactamente lo mismo: elegir un carácter y meterlo donde está el
// cursor. Copiarlo dos veces es como acabamos con dos versiones del mapeo de
// "inactivo".
//
// EL DETALLE QUE LO HACE FUNCIONAR: `onMouseDown` con `preventDefault` en TODO
// lo que se pulsa. Sin eso, pulsar el botón le quita el foco al cuadro de
// texto, el navegador se lleva la selección con él, y el emoji acaba al final
// del texto en vez de donde estaba el cursor. Es el mismo motivo por el que la
// barra de formato ya lo hacía.
import { useEffect, useRef, useState } from 'react';
import { Smile } from 'lucide-react';
import { buscarEmoticonos } from '../emoticonos.js';

export default function EmojiPicker({ onElegir, es = true, disabled = false, alPulsar, estiloBoton }) {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const caja = useRef(null);

  // Cerrar al pulsar fuera y con Escape. Sin lo segundo, un panel abierto en
  // una pantalla pequeña tapa el cuadro de texto y no hay forma de quitarlo sin
  // volver a apuntar al botón.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc); };
  }, [abierto]);

  const grupos = buscarEmoticonos(busca);

  const btn = estiloBoton || {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 6,
    border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
    color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 0,
  };

  return (
    <span ref={caja} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        title={es ? 'Emoticonos' : 'Emojis'}
        disabled={disabled}
        // Guarda la selección ANTES de que el navegador mueva el foco.
        onMouseDown={(e) => { e.preventDefault(); if (alPulsar) alPulsar(); }}
        onClick={() => { setAbierto((v) => !v); setBusca(''); }}
        style={btn}
      >
        <Smile size={13} />
      </button>
      {abierto && (
        <div
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60,
            width: 268, maxHeight: 268, overflowY: 'auto',
            background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)',
            borderRadius: 10, padding: '.5rem', boxShadow: '0 10px 30px rgba(0,0,0,.45)',
          }}
        >
          <input
            autoFocus
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder={es ? 'Buscar (cita, gracias, auto…)' : 'Search (date, thanks, auto…)'}
            style={{
              width: '100%', boxSizing: 'border-box', marginBottom: '.45rem',
              background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
              borderRadius: 7, color: 'var(--c-text,#c8dff0)', fontSize: '.72rem',
              padding: '.3rem .45rem', fontFamily: 'inherit', outline: 'none',
            }}
          />
          {grupos.length === 0 && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', padding: '.4rem' }}>
              {es ? 'Ninguno coincide.' : 'No matches.'}
            </div>
          )}
          {grupos.map((g) => (
            <div key={g.id} style={{ marginBottom: '.35rem' }}>
              <div style={{
                fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em',
                color: 'var(--c-text3,#45688a)', marginBottom: 2, fontFamily: 'var(--font-mono)',
              }}>{es ? g.es : g.en}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {g.emojis.map((x) => (
                  <button
                    key={x.e}
                    type="button"
                    title={es ? x.es : x.en}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onElegir(x.e); setAbierto(false); }}
                    style={{
                      width: 30, height: 30, fontSize: '1.05rem', lineHeight: 1,
                      background: 'transparent', border: '1px solid transparent',
                      borderRadius: 7, cursor: 'pointer', padding: 0,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface2,#112538)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >{x.e}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
